<div class="sc-wrap" [ngStyle]="themeVars">

	<div class="sc-card-badge">
		<i class="material-icons">event</i>
	</div>

	<!-- ── Header ──────────────────────────────────────────────────────── -->
	<div class="sc-header">
		<button class="sc-nav-btn" (click)="prevMonth()" title="Previous month">
			<i class="material-icons">chevron_left</i>
		</button>
		<div class="sc-title-group">
			<span class="sc-month-label">{{ monthLabel }}</span>
			<button class="sc-today-btn" *ngIf="!isViewingCurrentMonth" (click)="goToday()" title="Jump to current month">
				<i class="material-icons">today</i> Jump to today
			</button>
			<button class="sc-today-btn" *ngIf="canEditTargets" (click)="openTargetEditor()" title="Set sales targets">
				<i class="material-icons">flag</i> Targets
			</button>
		</div>
		<button class="sc-nav-btn" (click)="nextMonth()" title="Next month">
			<i class="material-icons">chevron_right</i>
		</button>
	</div>

	<!-- ── Summary bar ──────────────────────────────────────────────────── -->
	<div class="sc-stats" *ngIf="!loading">
		<div class="sc-stat">
			<span class="sc-stat-val" *ngIf="unitsPrimary">{{ totalUnits | number:'1.0-0' }}</span>
			<span class="sc-stat-val" *ngIf="!unitsPrimary">{{ totalMonth | currency:'CAD':'symbol-narrow':'1.0-0' }}</span>
			<span class="sc-stat-sub" *ngIf="unitsPrimary">{{ totalMonth | currency:'CAD':'symbol-narrow':'1.0-0' }}</span>
			<span class="sc-stat-sub" *ngIf="!unitsPrimary && totalUnits > 0">{{ totalUnits | number:'1.0-0' }} units</span>
			<span class="sc-stat-lbl">{{ unitsPrimary ? 'Month Units' : 'Month Sales' }}</span>
		</div>
		<div class="sc-stat sc-stat--kpi"
			 [class.sc-stat--up]="monthPct !== null && monthPct >= 0"
			 [class.sc-stat--dn]="monthPct !== null && monthPct < 0">
			<span class="sc-stat-val sc-stat-kpi-val" *ngIf="monthPct !== null">
				<i class="material-icons sc-stat-kpi-arrow">{{ monthPct >= 0 ? 'trending_up' : 'trending_down' }}</i>
				{{ formatPct(monthPct) }}
			</span>
			<span class="sc-stat-val sc-stat-kpi-val sc-stat-kpi-val--muted" *ngIf="monthPct === null">—</span>
			<span class="sc-stat-sub" *ngIf="monthPct !== null">vs last year</span>
			<span class="sc-stat-lbl">YoY Growth</span>
		</div>
		<div class="sc-stat">
			<span class="sc-stat-val" *ngIf="unitsPrimary">{{ bestUnits | number:'1.0-0' }}</span>
			<span class="sc-stat-val" *ngIf="!unitsPrimary">{{ bestDay | currency:'CAD':'symbol-narrow':'1.0-0' }}</span>
			<span class="sc-stat-sub" *ngIf="unitsPrimary && bestUnitsDate">{{ bestUnitsDate | date:'EEE, MMM d' }}</span>
			<span class="sc-stat-sub" *ngIf="!unitsPrimary && bestDayDate">{{ bestDayDate | date:'EEE, MMM d' }}</span>
			<span class="sc-stat-lbl">Best Day</span>
		</div>
		<div class="sc-stat">
			<span class="sc-stat-val" *ngIf="unitsPrimary">{{ avgUnits | number:'1.0-0' }}</span>
			<span class="sc-stat-val" *ngIf="!unitsPrimary">{{ avgDay | currency:'CAD':'symbol-narrow':'1.0-0' }}</span>
			<span class="sc-stat-sub" *ngIf="unitsPrimary">{{ avgDay | currency:'CAD':'symbol-narrow':'1.0-0' }} / day</span>
			<span class="sc-stat-sub" *ngIf="!unitsPrimary && avgUnits > 0">{{ avgUnits | number:'1.0-0' }} u / day</span>
			<span class="sc-stat-lbl">Avg / Day</span>
		</div>
		<div class="sc-stat">
			<span class="sc-stat-val">{{ salesDays }}</span>
			<span class="sc-stat-lbl">Sales Days</span>
		</div>
		<div class="sc-stat sc-stat--target" *ngIf="monthTarget > 0">
			<span class="sc-stat-val">{{ monthTarget | currency:'CAD':'symbol-narrow':'1.0-0' }}</span>
			<span class="sc-stat-sub" [class.sc-pace-up]="onPace" [class.sc-pace-dn]="!onPace">
				{{ targetAttainment | percent:'1.0-0' }} attained
			</span>
			<div class="sc-target-bar" title="Progress vs target — marker shows where the month should be today">
				<div class="sc-target-fill" [class.sc-target-fill--behind]="!onPace" [style.width.%]="targetFillPct"></div>
				<div class="sc-target-pace" *ngIf="targetPacePct > 0 && targetPacePct < 100" [style.left.%]="targetPacePct"></div>
			</div>
			<span class="sc-stat-lbl">Month Target</span>
		</div>
	</div>

	<!-- ── Spinner ──────────────────────────────────────────────────────── -->
	<div class="sc-loading" *ngIf="loading">
		<svg class="spinner" width="40px" height="40px" viewBox="0 0 66 66">
			<circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
		</svg>
	</div>

	<!-- ── Main body: calendar + optional drill panel ──────────────────── -->
	<div class="sc-body" *ngIf="!loading">

		<!-- Calendar grid -->
		<div class="sc-calendar-col">

			<div class="sc-grid">
				<!-- Day headers -->
				<div class="sc-dow" *ngFor="let d of DOW">{{ d }}</div>

				<!-- Cells -->
				<ng-container *ngFor="let week of weeks">
					<div class="sc-cell"
						*ngFor="let day of week"
						[class.sc-other-month]="!day.isCurrentMonth"
						[class.sc-today]="day.isToday"
						[class.sc-has-sales]="day.isCurrentMonth && day.invoices > 0"
						[class.sc-clickable]="day.isCurrentMonth && day.invoices > 0"
						[class.sc-drill-active]="drillOpen && drillDate === day.date.toLocaleDateString('en-CA')"
						[style.background]="day.isCurrentMonth && day.invoices > 0 ? cellBg(day.intensity) : null"
						[style.color]="day.isCurrentMonth && day.invoices > 0 ? cellTextColor(day.intensity) : null"
						(click)="onDayClick(day)">

						<span class="sc-day-num">{{ day.dayNum }}</span>

						<ng-container *ngIf="day.isCurrentMonth && day.invoices > 0">
							<span class="sc-sales-val">{{ unitsPrimary ? formatUnits(day.units) : formatDollars(day.dollars, day.invoices) }}</span>
							<span class="sc-units-val" *ngIf="day.dollars > 0">{{ unitsPrimary ? formatDollars(day.dollars, day.invoices) : formatUnits(day.units) }}</span>
							<span class="sc-pct-val" *ngIf="day.dollarPct !== null"
								[class.sc-pct--up]="day.dollarPct >= 0"
								[class.sc-pct--down]="day.dollarPct < 0">
								{{ formatPct(day.dollarPct) }}
							</span>
						</ng-container>
					</div>
				</ng-container>
			</div>

			<!-- Weekly progress vs month target -->
			<div class="sc-week-progress" *ngIf="monthTarget > 0 && weekSummaries.length">
				<div class="sc-week-chip" *ngFor="let w of weekSummaries; let i = index"
					[class.sc-week-chip--ahead]="w.started && w.cumPct >= w.expectedPct"
					[class.sc-week-chip--behind]="w.started && w.cumPct < w.expectedPct"
					[class.sc-week-chip--current]="w.isCurrent">
					<span class="sc-week-chip-lbl">Wk {{ i + 1 }} · {{ w.label }}</span>
					<span class="sc-week-chip-val">{{ w.total | currency:'CAD':'symbol-narrow':'1.0-0' }}</span>
					<span class="sc-week-chip-sub" *ngIf="w.started">
						{{ w.cumPct | percent:'1.0-0' }} of target<ng-container *ngIf="w.expectedPct > 0 && w.expectedPct < 1"> · pace {{ w.expectedPct | percent:'1.0-0' }}</ng-container>
					</span>
					<span class="sc-week-chip-sub sc-week-chip-sub--muted" *ngIf="!w.started">upcoming</span>
				</div>
			</div>

			<!-- Legend -->
			<div class="sc-legend" *ngIf="salesDays > 0">
				<span class="sc-legend-lbl">Low</span>
				<div class="sc-legend-bar"></div>
				<span class="sc-legend-lbl">High</span>
			</div>
		</div>

	</div><!-- /sc-body -->

	<!-- ── Drill-down modal ──────────────────────────────────────────── -->
	<div #drillBackdrop class="sc-drill-modal-backdrop" *ngIf="drillOpen" [ngStyle]="themeVars" (click)="closeDrill()">
		<div class="sc-drill-panel sc-drill-panel--modal" (click)="$event.stopPropagation()">

			<!-- Panel header -->
			<div class="sc-drill-header">
				<button class="sc-drill-back" *ngIf="canDrillBack" (click)="drillBack()" title="Back">
					<i class="material-icons">arrow_back</i>
				</button>
				<div class="sc-drill-breadcrumbs">
					<button class="sc-bc-item sc-bc-date"
						(click)="breadcrumbBack(drillMode === 'brand' ? 'brands' : 'salesmen')">
						<i class="material-icons">event</i> {{ drillDateLabel }}
					</button>
					<!-- Brand path -->
					<ng-container *ngIf="drillLevel === 'customers' || drillLevel === 'invoices' || drillLevel === 'lines'">
						<i class="material-icons sc-bc-sep">chevron_right</i>
						<button class="sc-bc-item" [class.sc-bc-active]="drillLevel === 'customers'"
							(click)="breadcrumbBack('customers')">
							{{ drillBrandName || 'All Brands' }}
						</button>
					</ng-container>
					<ng-container *ngIf="drillLevel === 'invoices' || drillLevel === 'lines'">
						<i class="material-icons sc-bc-sep">chevron_right</i>
						<button class="sc-bc-item" [class.sc-bc-active]="drillLevel === 'invoices'"
							(click)="breadcrumbBack('invoices')">
							{{ drillDebtorName }}
						</button>
					</ng-container>
					<ng-container *ngIf="drillLevel === 'lines'">
						<i class="material-icons sc-bc-sep">chevron_right</i>
						<span class="sc-bc-item sc-bc-active">Inv #{{ drillTransno }}</span>
					</ng-container>
					<!-- Salesman path -->
					<ng-container *ngIf="drillLevel === 'sm-customers' || drillLevel === 'sm-invoices' || drillLevel === 'sm-lines'">
						<i class="material-icons sc-bc-sep">chevron_right</i>
						<button class="sc-bc-item" [class.sc-bc-active]="drillLevel === 'sm-customers'"
							(click)="breadcrumbBack('sm-customers')">
							<i class="material-icons">person</i> {{ drillSalesmanName }}
						</button>
					</ng-container>
					<ng-container *ngIf="drillLevel === 'sm-invoices' || drillLevel === 'sm-lines'">
						<i class="material-icons sc-bc-sep">chevron_right</i>
						<button class="sc-bc-item" [class.sc-bc-active]="drillLevel === 'sm-invoices'"
							(click)="breadcrumbBack('sm-invoices')">
							{{ drillDebtorName }}
						</button>
					</ng-container>
					<ng-container *ngIf="drillLevel === 'sm-lines'">
						<i class="material-icons sc-bc-sep">chevron_right</i>
						<span class="sc-bc-item sc-bc-active">Inv #{{ drillTransno }}</span>
					</ng-container>
				</div>
				<button class="sc-drill-close" (click)="closeDrill()" title="Close">
					<i class="material-icons">close</i>
				</button>
			</div>

			<!-- ── Day KPI ribbon — top-level summary for sales managers ───── -->
			<div class="sc-day-kpis" [class.sc-units-primary]="unitsPrimary" *ngIf="drillDayCell">
				<div class="sc-day-kpi sc-day-kpi--dollars">
					<span class="sc-day-kpi-val">{{ drillDayCell.dollars | currency:'CAD':'symbol-narrow':'1.0-0' }}</span>
					<span class="sc-day-kpi-lbl">Day Sales</span>
					<span class="sc-day-kpi-sub" *ngIf="drillDayCell.dollarPct !== null"
						[class.sc-up]="drillDayCell.dollarPct >= 0"
						[class.sc-dn]="drillDayCell.dollarPct < 0">
						<i class="material-icons">{{ drillDayCell.dollarPct >= 0 ? 'arrow_upward' : 'arrow_downward' }}</i>
						{{ formatPct(drillDayCell.dollarPct) }} YoY
					</span>
				</div>
				<div class="sc-day-kpi">
					<span class="sc-day-kpi-val">{{ drillDayCell.invoices | number:'1.0-0' }}</span>
					<span class="sc-day-kpi-lbl">Invoices</span>
				</div>
				<div class="sc-day-kpi sc-day-kpi--units">
					<span class="sc-day-kpi-val">{{ drillDayCell.units | number:'1.0-0' }}</span>
					<span class="sc-day-kpi-lbl">Units</span>
				</div>
				<div class="sc-day-kpi">
					<span class="sc-day-kpi-val">{{ drillAov | currency:'CAD':'symbol-narrow':'1.0-0' }}</span>
					<span class="sc-day-kpi-lbl">Avg Order</span>
				</div>
				<div class="sc-day-kpi sc-day-kpi--hide-sm">
					<span class="sc-day-kpi-val">#{{ drillDayRank }}<span class="sc-day-kpi-rank-of"> / {{ drillDayRankOf }}</span></span>
					<span class="sc-day-kpi-lbl">Rank</span>
					<span class="sc-day-kpi-sub" *ngIf="drillVsMonthAvg !== null"
						[class.sc-up]="drillVsMonthAvg >= 0"
						[class.sc-dn]="drillVsMonthAvg < 0">
						{{ formatPct(drillVsMonthAvg) }} vs avg
					</span>
				</div>
			</div>

			<!-- Mode tabs: Brands ⇄ Salesmen -->
			<div class="sc-drill-tabs">
				<button class="sc-drill-tab" [class.sc-drill-tab--active]="drillMode === 'brand'"
					(click)="switchMode('brand')">
					<i class="material-icons">storefront</i> Brands
				</button>
				<button class="sc-drill-tab" [class.sc-drill-tab--active]="drillMode === 'salesman'"
					(click)="switchMode('salesman')">
					<i class="material-icons">groups</i> Salesmen
				</button>
			</div>

			<!-- List meta: count + visible total + top performer -->
			<div class="sc-drill-meta" *ngIf="!drillLoading && drillLevel !== 'lines' && drillLevel !== 'sm-lines' && drillListMeta.count > 0">
				<span class="sc-drill-meta-count">
					<strong>{{ drillListMeta.count }}</strong> {{ drillListMeta.label }}
					<span class="sc-drill-meta-sep">•</span>
					<strong>{{ drillListMeta.total | currency:'CAD':'symbol-narrow':'1.0-0' }}</strong>
				</span>
				<span class="sc-drill-meta-top" *ngIf="drillTopRow">
					<i class="material-icons">emoji_events</i>
					<span class="sc-drill-meta-top-name">{{ drillTopRow.name }}</span>
					<span class="sc-drill-meta-top-val">{{ drillTopRow.sales | currency:'CAD':'symbol-narrow':'1.0-0' }}</span>
				</span>
			</div>

			<!-- Spinner -->
			<div class="sc-drill-spinner" *ngIf="drillLoading">
				<svg class="spinner" width="32px" height="32px" viewBox="0 0 66 66">
					<circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
				</svg>
			</div>

			<!-- ── Brands ──────────────────────────────── -->
			<div class="sc-drill-list" *ngIf="!drillLoading && drillLevel === 'brands'">
				<div class="sc-drill-list-header">
					<span>Brand</span>
					<span>Units</span>
					<span>Sales</span>
				</div>
				<div class="sc-drill-row" *ngFor="let b of drillBrands" (click)="onBrandClick(b)">
					<div class="sc-drill-row-main">
						<span class="sc-drill-name">{{ b.brand }}</span>
						<span class="sc-drill-units">{{ b.units | number:'1.0-0' }}</span>
						<span class="sc-drill-sales">{{ b.sales | currency:'CAD':'symbol-narrow':'1.0-2' }}</span>
					</div>
					<div class="sc-drill-bar-wrap">
						<div class="sc-drill-bar" [style.width.%]="salesBar(b.sales, drillBrandTotal)"></div>
					</div>
				</div>
				<div class="sc-drill-empty" *ngIf="drillBrands.length === 0">No brand data for this day.</div>
			</div>

			<!-- ── Salesmen leaderboard (with YoY + MTD) ─────────────────── -->
			<div class="sc-drill-list sc-drill-list--sm" *ngIf="!drillLoading && drillLevel === 'salesmen'">
				<div class="sc-drill-list-header sc-sm-header">
					<span>Salesman</span>
					<span>Units</span>
					<span>Day Sales</span>
					<span>YoY</span>
					<span>MTD</span>
				</div>
				<div class="sc-drill-row sc-sm-row" *ngFor="let sm of drillSalesmen; let i = index"
					(click)="onSalesmanClick(sm)">
					<div class="sc-drill-row-main sc-sm-row-main">
						<span class="sc-drill-name sc-sm-name">
							<span class="sc-sm-rank" *ngIf="sm.sales > 0">{{ i + 1 }}</span>
							{{ sm.salesmanname }}
						</span>
						<span class="sc-drill-units">{{ sm.units | number:'1.0-0' }}</span>
						<span class="sc-drill-sales">{{ sm.sales | currency:'CAD':'symbol-narrow':'1.0-0' }}</span>
						<span class="sc-sm-yoy"
							[class.sc-pct--up]="salesmanYoy(sm) !== null && salesmanYoy(sm)! >= 0"
							[class.sc-pct--down]="salesmanYoy(sm) !== null && salesmanYoy(sm)! < 0">
							<ng-container *ngIf="salesmanYoy(sm) !== null">
								<i class="material-icons sc-sm-yoy-arrow">{{ salesmanYoy(sm)! >= 0 ? 'arrow_upward' : 'arrow_downward' }}</i>
								{{ formatPct(salesmanYoy(sm)) }}
							</ng-container>
							<span class="sc-sm-yoy-none" *ngIf="salesmanYoy(sm) === null">—</span>
						</span>
						<span class="sc-sm-mtd">{{ sm.mtd_sales | currency:'CAD':'symbol-narrow':'1.0-0' }}</span>
					</div>
					<div class="sc-drill-bar-wrap">
						<div class="sc-drill-bar" [style.width.%]="salesBar(sm.sales, drillSalesmenTotal)"></div>
					</div>
				</div>
				<div class="sc-drill-empty" *ngIf="drillSalesmen.length === 0">No salesman activity for this day.</div>
			</div>

			<!-- ── Customers ──────────────────────────── -->
			<div class="sc-drill-list" *ngIf="!drillLoading && (drillLevel === 'customers' || drillLevel === 'sm-customers')">
				<div class="sc-drill-list-header">
					<span>Customer</span>
					<span>Units</span>
					<span>Sales</span>
				</div>
				<div class="sc-drill-row" *ngFor="let c of drillCustomers"
					(click)="drillLevel === 'sm-customers' ? onSalesmanCustomerClick(c) : onCustomerClick(c)">
					<div class="sc-drill-row-main">
						<span class="sc-drill-name">{{ c.brname }}</span>
						<span class="sc-drill-units">{{ c.units | number:'1.0-0' }}</span>
						<span class="sc-drill-sales">{{ c.sales | currency:'CAD':'symbol-narrow':'1.0-2' }}</span>
					</div>
					<div class="sc-drill-bar-wrap">
						<div class="sc-drill-bar" [style.width.%]="salesBar(c.sales, drillCustomerTotal)"></div>
					</div>
				</div>
				<div class="sc-drill-empty" *ngIf="drillCustomers.length === 0">No customer data.</div>
			</div>

			<!-- ── Invoices ───────────────────────────── -->
			<div class="sc-drill-list" *ngIf="!drillLoading && (drillLevel === 'invoices' || drillLevel === 'sm-invoices')">
				<div class="sc-drill-list-header">
					<span>Invoice #</span>
					<span>Units</span>
					<span>Sales</span>
				</div>
				<div class="sc-drill-row" *ngFor="let inv of drillInvoices"
					(click)="drillLevel === 'sm-invoices' ? onSalesmanInvoiceClick(inv) : onInvoiceClick(inv)">
					<div class="sc-drill-row-main">
						<span class="sc-drill-name sc-drill-inv">#{{ inv.transno }}</span>
						<span class="sc-drill-units">{{ inv.units | number:'1.0-0' }}</span>
						<span class="sc-drill-sales">{{ inv.sales | currency:'CAD':'symbol-narrow':'1.0-2' }}</span>
					</div>
					<div class="sc-drill-bar-wrap">
						<div class="sc-drill-bar" [style.width.%]="salesBar(inv.sales, drillInvoiceTotal)"></div>
					</div>
				</div>
				<div class="sc-drill-empty" *ngIf="drillInvoices.length === 0">No invoices found.</div>
			</div>

			<!-- ── Line items ─────────────────────────── -->
			<div class="sc-drill-lines" *ngIf="!drillLoading && (drillLevel === 'lines' || drillLevel === 'sm-lines')">
				<table class="sc-lines-table">
					<thead>
						<tr>
							<th>Stock ID</th>
							<th>Description</th>
							<th class="text-right">Qty</th>
							<th class="text-right">Unit $</th>
							<th class="text-right">Line Total</th>
						</tr>
					</thead>
					<tbody>
						<tr *ngFor="let line of drillLines">
							<td class="sc-line-stockid">{{ line.stockid }}</td>
							<td class="sc-line-desc">{{ line.description }}</td>
							<td class="text-right">{{ line.qty | number:'1.0-2' }}</td>
							<td class="text-right">{{ line.unitprice | currency:'CAD':'symbol-narrow':'1.2-2' }}</td>
							<td class="text-right sc-line-total">{{ line.linetotal | currency:'CAD':'symbol-narrow':'1.2-2' }}</td>
						</tr>
					</tbody>
					<tfoot *ngIf="drillLines.length">
						<tr>
							<td colspan="4" class="text-right sc-lines-footer-lbl">Invoice Total</td>
							<td class="text-right sc-lines-footer-val">
								{{ lineTotal(drillLines) | currency:'CAD':'symbol-narrow':'1.2-2' }}
							</td>
						</tr>
					</tfoot>
				</table>
				<div class="sc-drill-empty" *ngIf="drillLines.length === 0">No line items found.</div>
			</div>

		</div><!-- /drill panel -->
	</div><!-- /sc-drill-modal-backdrop -->

</div>
